import React, { ChangeEvent, FC, FormEvent, useContext, useEffect, useState } from 'react';
import { getKcClsx } from 'keycloakify/login/lib/kcClsx';
import { PageProps } from 'keycloakify/login/pages/PageProps';
import ArrowLeft16 from '@bankiru/icons/react/ArrowLeft16';
import ArrowLeft24 from '@bankiru/icons/react/ArrowLeft24';
import { Flexbox } from '@bankiru/ui-kit/components/Flexbox';
import { InputPipeMask } from '@bankiru/ui-kit/components/Form/InputPipeMask';
import { Indent } from '@bankiru/ui-kit/components/Indent';
import Button from '@bankiru/ui-kit/core/Button';
import FormField from '@bankiru/ui-kit/core/FormField';
import Text from '@bankiru/ui-kit/core/Text';

import { ThemeContext } from '../../../../../context/ThemeContextWrapper';
import { formatPhoneNumber } from '../../../../../helpers/formatPhoneNumber';
import { getInputStatus } from '../../../../../helpers/getInputStatus';
import { getSavedConsents } from '../../../../../helpers/getSavedConsents';
import { hideInputElementsForWebVisor } from '../../../../../helpers/hideInputElementsForWebVisor';
import MobileIdLogo from '../../../../../img/mobile-id-logo.svg';
import { ConsentKeys } from '../../../../../types/consents';
import { I18n } from '../../../../i18n';
import { KcContext } from '../../../../KcContext';
import { RU_PHONE_REGEX } from './constants';

type MobileNumberFormProps = {
	backUrl: string;
	checkedConsents: Record<string, boolean>;
} & PageProps<Extract<KcContext, { pageId: 'login-username.ftl' }>, I18n>;

export const PHONE_MASK = {
	mask: '+7 999 999-99-99',
	maskPlaceholder: '_',
};

const checkedConsents = getSavedConsents();

export const MobileNumberForm: FC<MobileNumberFormProps> = props => {
	const { kcContext, i18n, doUseDefaultCss, classes, backUrl } = props;

	const { kcClsx } = getKcClsx({
		doUseDefaultCss,
		classes,
	});

	const { realm, url, usernameHidden, login, messagesPerField } = kcContext;

	const { msg, msgStr } = i18n;

	const { isMobile } = useContext(ThemeContext);
	const initialPhoneNumber = login.username;

	const [isPhoneNumberSubmitted, setIsPhoneNumberSubmitted] = useState(false);
	const [phone, setPhone] = useState<string>(
		login.username ? formatPhoneNumber(initialPhoneNumber) : '+7',
	);
	const [error, setError] = useState('');

	const onFormSubmit = (event: FormEvent) => {
		const clearPhone = phone.replace(/[\s-()]*/g, '');

		if (!RU_PHONE_REGEX.exec(clearPhone)) {
			event.preventDefault();

			setError(msgStr('invalidPhoneNumber'));

			return false;
		}

		setIsPhoneNumberSubmitted(true);

		return true;
	};

	// todo PipeInput в uikit не прокидывает проперти как ожидается
	useEffect(() => {
		const phoneInput = document.getElementsByTagName('input')[0] as HTMLInputElement | null;

		if (phoneInput) {
			phoneInput.type = 'tel';
			phoneInput.autofocus = true;

			setTimeout(() => {
				if (!isMobile) {
					phoneInput.focus();
				}
			}, 0);
		}
	}, [isMobile]);

	useEffect(() => {
		hideInputElementsForWebVisor();
	}, []);

	if (!realm.password) {
		return null;
	}

	const consentsKeys = Object.keys(checkedConsents) as Array<ConsentKeys>;
	const consentsQuery = consentsKeys
		.map(key => `${key}=${checkedConsents[key] ? 1 : 0}`)
		.join('&');

	return (
		<div>
			<Text size={isMobile ? 'fs12' : 'fs14'} color="secondary">
				{msg('loginDescriptionOtp')}
			</Text>
			<form id="kc-form-login" action={`${url.loginAction}&${consentsQuery}`} method="post">
				{!usernameHidden && (
					<div className={kcClsx('kcFormGroupClass')}>
						<FormField
							id="phoneNumber"
							label={msgStr('phoneNumber')}
							type="tel"
							value={phone}
							onChange={(event: ChangeEvent<HTMLInputElement>) => {
								setPhone(event.target.value);
								setError('');
							}}
							isFloatingLabel
							autoFocus
							status={
								getInputStatus(messagesPerField, 'username', error) ??
								({ type: 'base' } as never)
							}
							autoComplete="off"
							size="h56"
							component={InputPipeMask}
							mask={PHONE_MASK}
						/>
						<input
							value={phone.replace(/[\s-()]/g, '')}
							type="hidden"
							id="username"
							name="username"
						/>
					</div>
				)}

				<div className={kcClsx('kcFormGroupClass', 'kcFormSettingClass')}>
					<div id="kc-form-options">
						{realm.rememberMe && !usernameHidden && (
							<div className="checkbox">
								<label>
									<input
										tabIndex={3}
										id="rememberMe"
										name="rememberMe"
										type="checkbox"
										defaultChecked={!!login.rememberMe}
									/>
									{msg('rememberMe')}
								</label>
							</div>
						)}
					</div>
				</div>
				<div id="kc-form-buttons" className={kcClsx('kcFormGroupClass')}>
					<Flexbox gap="16">
						<Button
							skin="white"
							size="h48"
							leftIcon={{
								size24: <ArrowLeft24 />,
								size16: <ArrowLeft16 />,
							}}
							href={backUrl}
							isDisabled={isPhoneNumberSubmitted}
						/>
						<Button
							size="h48"
							type="submit"
							name="login"
							isDisabled={isPhoneNumberSubmitted}
							isFullWidth
						>
							{msgStr('doLogIn')}
						</Button>
					</Flexbox>
				</div>
				<Indent margin="24 0 80 0">
					<Flexbox gap="4">
						<img src={MobileIdLogo} width="16" height="16" alt="" />
						<Text tagName="span" color="secondary" size="fs12">
							Вход через сервис Мобильный ID.
						</Text>
					</Flexbox>
					<Text color="secondary" size="fs12">
						Ваши данные защищены шифрованием.
					</Text>
				</Indent>
			</form>
		</div>
	);
};
